import { type FC, useCallback, useEffect } from 'react'

import { useUnit } from 'effector-react'

import PlusIcon from '@/assets/icons/icon-plus.svg'
import RefreshIcon from '@/assets/icons/icon-refresh.svg'
import { Button } from '@/components/ui/button.tsx'
import { Dialog } from '@/components/ui/dialog.tsx'
import { Tooltip } from '@/components/ui/tooltip.tsx'
import { DIALOG_TYPE } from '@/dialogs/constants.ts'
import type { DialogProps } from '@/dialogs/types'
import { openDialog } from '@/models/dialogs.ts'
import { $hasEditVaultPermission } from '@/models/permissions.ts'
import {
  $isRefetchAvailable,
  $selectedMediaIds,
  refetchVaultMedias,
  resetSelectedMedia,
} from '@/models/vault/medias'
import { closeVault, openVault } from '@/models/vault/vault.ts'
import type { MediaSelection } from '@/modules/message/components/editor/fields/message-media-dropdown.tsx'
import { VaultCategorySelect } from '@/modules/vault/ui/category/vault-category-select.tsx'
import { VaultMediaSearchBox } from '@/modules/vault/ui/media/vault-media-search-box.tsx'
import { VaultMedia } from '@/modules/vault/ui/media/vault-media.tsx'

interface Props extends DialogProps {
  onSelect: (selected: MediaSelection[]) => void
}

export const MessageMediaSelectionDialog: FC<Props> = ({
  isOpen,
  onSelect,
  onClose,
}) => {
  const hasEditVaultPermission = useUnit($hasEditVaultPermission)
  const isRefetchAvailable = useUnit($isRefetchAvailable)
  const selectedMediaIds = useUnit($selectedMediaIds)
  const handleAddMediaClick = useCallback(() => {
    openDialog({
      type: DIALOG_TYPE.ADD_MEDIA,
    })
  }, [])

  const handleSelect = useCallback(() => {
    onSelect(
      selectedMediaIds.map((id, index) => ({
        mediaId: id,
        sorting: index + 1,
      })),
    )
    onClose()
  }, [onClose, onSelect, selectedMediaIds])

  useEffect(() => {
    if (isOpen) {
      openVault()
    } else {
      closeVault()
      resetSelectedMedia()
    }
  }, [isOpen])

  return (
    <Dialog
      panelClassName="max-h-[calc(100%-32px)] flex flex-col"
      childrenClassName="overflow-hidden flex flex-col space-y-6"
      wideXl
      bigDialog
      show={isOpen}
    >
      <Dialog.Header>
        <Dialog.Title>Select Media</Dialog.Title>
      </Dialog.Header>

      <div>
        <div className="mb-3 flex items-center">
          <VaultCategorySelect />
        </div>
        <div className="flex items-center">
          <VaultMediaSearchBox className="py-2" />
          <Tooltip
            content="Refresh media"
            disabled={!isRefetchAvailable}
          >
            <Button
              onClick={refetchVaultMedias}
              variant="ghost"
              className="text-text-default px-4"
              disabled={!isRefetchAvailable}
            >
              <RefreshIcon className="h-4 w-4" />
            </Button>
          </Tooltip>
          {hasEditVaultPermission && (
            <Button
              onClick={handleAddMediaClick}
              className="shrink-0"
            >
              <PlusIcon /> Add Media
            </Button>
          )}
        </div>
      </div>

      <VaultMedia
        showTypeSelection={false}
        showTitle={false}
        isForSelection
      />

      <div className="grid grid-cols-2 gap-2">
        <Button
          variant="outline"
          onClick={onClose}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSelect}
          testId="message-media-continue-button"
        >
          Continue
        </Button>
      </div>
    </Dialog>
  )
}
